import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import React, { useContext } from 'react';

import { eadConfig } from 'modules/archival-aid/archival-aid-children/ead-grid-config';
import { archivalAidConfig } from 'modules/archival-aid/archival-aid-grid';
import { archivalCollectionConfig } from 'modules/archival-collection/archival-collection-grid';
import { archivalUnitConfig } from 'modules/archival-unit/archival-unit-grid';
import { ArchivalAidMetadata, DomainObject } from 'types';
import { getNodeId } from 'utils/ead-utils';
import { GridEntityConfig } from '../grid-types';
import { Breadcrumb, BreadcrumbsContext } from './context/curmbs-context';
import { HierarchyContext } from './context/hierarchy-context';
import { useArchivalAidDetail } from './hooks/aid-detail-hook';
import { useArchivalCollectionDetail } from './hooks/archival-collection-hook';
import { useArchivalUnitDetail } from './hooks/archival-unit-detail-hook';
import { useEadDetail } from './hooks/ead-detail-hook';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridRightPanelRoot: {
      minHeight: 'calc(100vh - 140px)',
      height: '100%',
      backgroundColor: 'white',
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      //marginTop: 60,
      overflow: 'auto',
      minWidth: 200,
    },
    rightPanelTitle: {
      fontSize: '14px !important',
      fontWeight: ('600 !important' as unknown) as 600,
      letterSpacing: '1px !important',
      textTransform: 'uppercase',
      //margin: '16px !important',
    },
    rightPanelIcon: {
      width: 30,
      height: 30,
      margin: 15,
    },
    buttons: {
      padding: 16,
      width: '100%',
    },
    activeNavigationButton: {
      alignSelf: 'baseline',
      width: '100%',
      fontWeight: 'bold',
    },
    navigationButton: {
      alignSelf: 'baseline',
      width: '100%',
    },
    actionButton: {
      marginTop: theme.spacing(),
      alignSelf: 'baseline',
      width: '100%',
    },
  })
);

export type ActiveEntityType = 'fund' | 'aid' | 'ead' | 'archival';

export const rightPanelConfig: Record<
  ActiveEntityType,
  GridEntityConfig<DomainObject, DomainObject>
> = {
  aid: archivalAidConfig,
  archival: archivalUnitConfig,
  ead: eadConfig,
  fund: archivalCollectionConfig,
};

const getArchivalAidId = (breadcrumbs: Breadcrumb[]): string | undefined => {
  const node = breadcrumbs.find((b) => b.level === 'aid')?.node;
  if (node) {
    if (node?.archivalAidMetadata?.id) {
      return node.archivalAidMetadata.id;
    }
    return getNodeId(
      (node as unknown) as DomainObject & { baseIdentifier: string }
    );
  }
};

const useCurrentDetails = () => {
  const {
    archivalAidId,
    archivalUnitId,
    eadId,
    archivalCollectionId,
  } = useContext(HierarchyContext);
  const collectionDetail = useArchivalCollectionDetail(archivalCollectionId);
  const aidDetail = useArchivalAidDetail(archivalAidId);
  const archivalUnitDetail = useArchivalUnitDetail(archivalUnitId);
  const eadDetail = useEadDetail(eadId);

  return {
    loading:
      collectionDetail.isLoading ||
      aidDetail.isLoading ||
      archivalUnitDetail.isLoading ||
      eadDetail.isLoading,
    collection: collectionDetail.data,
    aid: aidDetail.data,
    archivalUnit: archivalUnitDetail.data,
    ead: eadDetail.data,
    refreshDetail: async () => {
      if (archivalCollectionId) {
        collectionDetail.refetch();
      }
      if (archivalAidId) {
        aidDetail.refetch();
      }
      if (archivalUnitId) {
        archivalUnitDetail.refetch();
      }
      if (eadId) {
        eadDetail.refetch();
      }
    },
  };
};

const useCurrentDetailConfig = (): {
  config: GridEntityConfig<DomainObject, DomainObject>;
  isEad?: boolean;
  rootArchivalAid?: ArchivalAidMetadata;
  refreshRootArchivalAid: () => Promise<unknown>;
} => {
  const { archivalAidId, archivalUnitId, eadId } = useContext(HierarchyContext);
  const breadcrumbs = useContext(BreadcrumbsContext);
  const aidId = getArchivalAidId(breadcrumbs);
  const rootArchivalAid = useArchivalAidDetail(aidId);

  if (eadId)
    return {
      config: eadConfig,
      isEad: true,
      rootArchivalAid: rootArchivalAid.data,
      refreshRootArchivalAid: rootArchivalAid.refetch,
    };
  if (archivalUnitId)
    return {
      config: archivalUnitConfig,
      rootArchivalAid: rootArchivalAid.data,
      refreshRootArchivalAid: rootArchivalAid.refetch,
    };
  if (archivalAidId)
    return {
      config: archivalAidConfig,
      rootArchivalAid: rootArchivalAid.data,
      refreshRootArchivalAid: rootArchivalAid.refetch,
    };
  return {
    config: archivalCollectionConfig,
    rootArchivalAid: rootArchivalAid.data,
    refreshRootArchivalAid: rootArchivalAid.refetch,
  };
};

export function GridRightPanel() {
  const classes = useStyles();

  const {
    archivalAidId,
    archivalUnitId,
    eadId,
    archivalCollectionId,
  } = useContext(HierarchyContext);

  const {
    config,
    rootArchivalAid,
    isEad,
    refreshRootArchivalAid,
  } = useCurrentDetailConfig();

  const {
    loading,
    collection,
    aid,
    archivalUnit,
    ead,
    refreshDetail,
  } = useCurrentDetails();

  const breadcrumbs = useContext(BreadcrumbsContext);

  const ActionButtons = config?.ActionButtons;

  if (loading) {
    return <></>;
  }

  if (!collection && !aid && !archivalUnit && !ead) {
    return <></>;
  }

  const item = eadId
    ? ead
    : archivalUnitId
    ? archivalUnit
    : archivalAidId
    ? aid
    : archivalCollectionId
    ? collection
    : undefined;

  return (
    <div className={classes.gridRightPanelRoot}>
      <LibraryBooksIcon className={classes.rightPanelIcon} />
      <div className={classes.buttons}>
        {ActionButtons && (
          <ActionButtons
            isEad={isEad}
            refreshDetail={refreshDetail}
            refreshList={async () => {}}
            refreshRootArchivalAid={
              refreshRootArchivalAid as () => Promise<void>
            }
            gridConfig={[config]}
            item={item}
            pathToItem={breadcrumbs[breadcrumbs.length - 1]?.to}
            rootArchivalAid={rootArchivalAid}
          />
        )}
      </div>
    </div>
  );
}
